import { useParams } from 'react-router-dom';

import { RunApprovalPopoverContent } from './run-approval-popover-content';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { RunStatus, Interval } from '../../../../backend/src/types/types';
import { blockedReasonMessage } from '../../../../backend/src/utils/billing-utils';
import { useMatterProcessingStore } from '../stores/matter-processing-store';
import { useSubmitRunApproval } from '@/api/mutations/useSubmitRunApproval';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { getRunType } from '@/helpers/runs';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { PipelineProcessingStageDescriptions, ProcessingStatus } from '@/new/lib/pipeline-status';

export function formatDurationEstimate(durationEstimate?: Interval | null): string {
  if (!durationEstimate) return '';
  const parts: string[] = [];
  const days = durationEstimate?.days ?? 0;
  const hours = durationEstimate?.hours ?? 0;
  const minutes = durationEstimate?.minutes ?? 0;
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  return parts.join(' ');
}

export function ProcessingStatusBadge({ allDocumentsCaseId }: { allDocumentsCaseId: string | undefined }) {
  const { matterId } = useParams();
  const { hasPermission } = useHasPermission();
  const { user } = useUserContext();

  // Check if user has permission to approve runs
  const canApproveRuns = hasPermission(Permission.RunApprove);
  const blockedMessage = user?.isBlocked ? blockedReasonMessage(user.blockedReason, user.blockedUntil) : null;

  const { mutateAsync: submitRunApproval, isLoading: isSubmittingApproval } = useSubmitRunApproval(
    allDocumentsCaseId ?? '',
    matterId ?? '',
  );

  const processingState = useMatterProcessingStore((state) =>
    matterId ? state.getProcessingState(matterId) : undefined,
  );

  if (!matterId || !allDocumentsCaseId) return null;
  const allProcessingRuns = processingState?.allProcessingRuns ?? [];

  // Separate awaiting approval runs from processing runs
  const awaitingApprovalRuns = allProcessingRuns.filter((run) => run.status === RunStatus.AwaitingApproval);
  const processingRuns = allProcessingRuns.filter((run) => run.status !== RunStatus.AwaitingApproval);

  const handleApprove = async (runId: string) => {
    await submitRunApproval({ runId, approvalGranted: true });
  };

  const handleDeny = async (runId: string) => {
    await submitRunApproval({ runId, approvalGranted: false });
  };

  // Determine badge appearance based on runs
  const hasAwaitingApproval = awaitingApprovalRuns.length > 0;
  const hasProcessing = processingRuns.length > 0;

  const approvalCount = awaitingApprovalRuns.length;
  const processingCount = processingRuns.length;
  const approvalText = `${approvalCount} Approval${approvalCount > 1 ? 's' : ''}`;

  let badgeText = '';
  if (hasAwaitingApproval && hasProcessing) {
    badgeText = `${approvalText}, ${processingCount} Processing`;
  } else if (hasAwaitingApproval) {
    badgeText = `${approvalText} Required`;
  } else {
    badgeText = `${processingCount} Processing`;
  }

  const badgeClassName = hasAwaitingApproval
    ? 'flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700 hover:bg-amber-100 hover:text-amber-800'
    : 'flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 hover:bg-blue-100';

  const badgeIcon = hasAwaitingApproval ? (
    <Icon className="lucide--shield-alert text-sm text-amber-600" />
  ) : (
    <Icon className="lucide--loader-2 h-4 w-4 animate-spin text-sm text-blue-600 duration-700" />
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className={badgeClassName}>
          {badgeIcon}
          {badgeText}
          <Icon className="lucide--arrow-right" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-w-96 overflow-y-auto p-0">
        <div className="flex flex-col">
          {awaitingApprovalRuns.map((run, index) => {
            const credits =
              (run.totalPages as number | undefined) ?? (run.successfulPagesCount || 0) + (run.failedPagesCount || 0);
            const isAllDocumentsCaseRun = run.caseId === allDocumentsCaseId;
            const runType = getRunType(run, isAllDocumentsCaseRun);

            return (
              <div key={run.id} className={`${index > 0 ? 'mt-2 border-t-2 border-gray-300' : ''} bg-gray-50/30`}>
                <RunApprovalPopoverContent
                  credits={credits}
                  durationEstimate={run.durationEstimate}
                  isSubmittingApproval={isSubmittingApproval}
                  onApprove={() => handleApprove(run.id)}
                  onDeny={() => handleDeny(run.id)}
                  runType={runType}
                  subsetName={run.subsetName}
                  requiresAdminApproval={run.requiresAdminApproval}
                  canApprove={canApproveRuns || run.createdById === user?.id}
                  blockedMessage={blockedMessage}
                />
              </div>
            );
          })}

          {/* Show processing runs */}
          {processingRuns.map((run, index) => {
            const stage = run.status as ProcessingStatus;
            const formattedEta = run.completedAtEstimate ? formatDate(run.completedAtEstimate, true, true) : null;
            const isAllDocumentsCaseRun = run.caseId === allDocumentsCaseId;
            const runType = getRunType(run, isAllDocumentsCaseRun);
            const credits =
              (run.totalPages as number | undefined) ?? (run.successfulPagesCount || 0) + (run.failedPagesCount || 0);

            const processingMessage = isAllDocumentsCaseRun ? (
              <p>Your Matter is being processed.</p>
            ) : (
              <p>
                Recontextualising facts for <strong>{run.subsetName}</strong>.
              </p>
            );

            // Add border-top and margin if this is not the first run overall
            const isFirstRun = awaitingApprovalRuns.length === 0 && index === 0;
            const separatorClass = isFirstRun ? '' : 'border-t-2 border-gray-300 mt-2';

            return (
              <div key={run.id} className={`${separatorClass} bg-gray-50/30`}>
                <div className="border-b border-blue-100 bg-blue-50 px-3.5 py-2.5">
                  <div className="flex items-center gap-2">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-900">Processing: {runType}</h4>
                      <div className="text-[11px] text-gray-600">{processingMessage}</div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 px-2 py-2">
                  <Badge className="rounded-lg border border-gray-200 bg-gray-50 py-0.75 text-gray-700">
                    <Icon className="lucide--file-text h-3 w-3 text-blue-700" />
                    <p className="text-xs text-gray-600">
                      Credits: <strong>{credits.toLocaleString?.() ?? credits}</strong>
                    </p>
                  </Badge>
                  {run.completedAtEstimate && (
                    <Badge className="rounded-lg border border-gray-200 bg-gray-50 py-0.75 text-gray-700">
                      <Icon className="lucide--clock h-3 w-3 text-purple-700" />
                      <p className="text-xs text-gray-600">
                        Estimated completion: <strong>{formattedEta}</strong>
                      </p>
                    </Badge>
                  )}
                  {stage && PipelineProcessingStageDescriptions[stage] && (
                    <Badge className="rounded-lg border border-gray-200 bg-gray-50 py-0.75 text-gray-700">
                      <Icon className="lucide--layers h-3 w-3 text-amber-700" />
                      <p className="text-xs text-gray-600">
                        Current stage: <strong>{PipelineProcessingStageDescriptions[stage].title}</strong>
                      </p>
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
